import React, { useContext, useEffect, useState } from "react";
import { AppContextType, WonmovContext } from "@/context/app-context";
import { videoRepo } from "@/repo/video-repo";
import { MovieCommentData } from "@/entry/server/movie";
import { Pagination } from "@/lib/api";
import likeIcon from "@public/img/like.svg";
import likeFillIcon from "@public/img/like-fill.svg";
import dislikeIcon from "@public/img/dislike.svg";
import dislikeFillIcon from "@public/img/dislike-fill.svg";
import UserWaveIcon from "@public/img/user-waves.svg";
import Tabs from "../../commom/tabs";
import "@/styles/comments.css";

const TABS = ["دیدگاه ها", "نقد و بررسی ها"];

export default function Comments({
    movieId,
    defaultComments,
    commentsPagination,
}: {
    movieId: number;
    defaultComments: MovieCommentData[];
    commentsPagination: Pagination;
}) {
    const { isLoggedIn, setNotification } = useContext<AppContextType>(WonmovContext);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [comments, setComments] = useState<MovieCommentData[]>(defaultComments);
    const [pagination, setPagination] = useState<Pagination>(commentsPagination);
    const [selectedTab, setSelectedTab] = useState(0);

    useEffect(function () {
        getMoreComments(true);
    }, [selectedTab]);

    const getMoreComments = function (reset?: boolean) {
        if (reset === undefined) {
            reset = false;
        }
        if (loadingMore && !reset) return;
        setLoadingMore(true);
        var page = reset ? 1 : pagination.next_page;
        videoRepo
            .getComments(movieId, selectedTab === 0 ? "false" : "true", page)
            .then(function (response) {
                setLoadingMore(false);
                if (response.status) {
                    setComments(
                        reset
                            ? response.data
                                ? response.data
                                : []
                            : function (prev) {
                                  return prev.concat(response.data ? response.data : []);
                              }
                    );
                    if (response.pagination) {
                        setPagination(response.pagination);
                    }
                }
            })
            .catch(function () {
                setLoadingMore(false);
            });
    };

    const likeComment = function (commentId: number) {
        if (!isLoggedIn) {
            setNotification({
                type: "danger",
                message: "برای لایک کردن دیدگاه باید وارد حساب کاربری خود شوید",
            });
            return;
        }

        if (loading) return;
        var targetComment = comments.find(function (c) {
            return c.id === commentId;
        });
        if (targetComment && targetComment.is_liked) return;
        setLoading(true);
        videoRepo.likeComment(commentId, "like").then(function (response) {
            setLoading(false);
            if (response.status) {
                setComments(function (prev) {
                    return prev.map(function (item) {
                        if (item.id !== commentId) return item;
                        var next = Object.assign({}, item);
                        next.like_count = item.like_count + 1;
                        next.is_liked = true;
                        if (item.is_disliked) {
                            next.dislike_count = item.dislike_count - 1;
                            next.is_disliked = false;
                        }
                        return next;
                    });
                });
            }
        }).catch(function () {
            setLoading(false);
        });
    };

    const dislikeComment = function (commentId: number) {
        if (!isLoggedIn) {
            setNotification({
                type: "danger",
                message: "برای دوست نداشتن دیدگاه باید وارد حساب کاربری خود شوید",
            });
            return;
        }

        if (loading) return;
        var targetComment = comments.find(function (c) {
            return c.id === commentId;
        });
        if (targetComment && targetComment.is_disliked) return;
        setLoading(true);
        videoRepo.likeComment(commentId, "dislike").then(function (response) {
            setLoading(false);
            if (response.status) {
                setComments(function (prev) {
                    return prev.map(function (item) {
                        if (item.id !== commentId) return item;
                        var next = Object.assign({}, item);
                        next.dislike_count = item.dislike_count + 1;
                        next.is_disliked = true;
                        if (item.is_liked) {
                            next.like_count = item.like_count - 1;
                            next.is_liked = false;
                        }
                        return next;
                    });
                });
            }
        }).catch(function () {
            setLoading(false);
        });
    };

    return (
        <div className="movie-comments">
            <section className="movie-comments__panel">
                <h2 className="movie-detail-section-title movie-comments__list-title">دیدگاه ها</h2>

                <Tabs tabs={TABS} selectedTab={selectedTab} onChange={setSelectedTab} />

                <div className="movie-comments__list">
                    {comments.map(function (item) {
                        if (selectedTab === 0) {
                            return (
                                <CommentItem
                                    key={item.id}
                                    comment={item}
                                    onLike={function () {
                                        likeComment(item.id);
                                    }}
                                    onDislike={function () {
                                        dislikeComment(item.id);
                                    }}
                                />
                            );
                        }
                        return (
                            <ReviewItem
                                key={item.id}
                                review={item}
                                onLike={function () {
                                    likeComment(item.id);
                                }}
                                onDislike={function () {
                                    dislikeComment(item.id);
                                }}
                            />
                        );
                    })}
                </div>

                {pagination && pagination.has_more && (
                    <button
                        type="button"
                        className="movie-comments__load-more"
                        onClick={function () {
                            getMoreComments(false);
                        }}
                        disabled={loadingMore}
                    >
                        {loadingMore ? <div className="loader" /> : "نمایش بیشتر"}
                    </button>
                )}
            </section>
        </div>
    );
}

function CommentItem({
    comment,
    onLike,
    onDislike,
}: {
    comment: MovieCommentData;
    onLike: () => void;
    onDislike: () => void;
}) {
    const [showText, setShowText] = useState(!comment.is_spoiler);

    var itemClass = "movie-comments__item";
    if (comment.parent_id > 0) {
        itemClass += " movie-comments__item--nested";
    }

    return (
        <div className={itemClass}>
            <div className="movie-comments__item-header">
                <p className="movie-comments__username">{comment.username}</p>
                <p className="movie-comments__date">{comment.created_at}</p>
            </div>

            <hr className="movie-comments__divider" />

            <div className="movie-comments__item-body">
                <div className="movie-comments__text-wrap">
                    <p className="movie-comments__text">
                        {showText ? comment.comment : "این دیدگاه حاوی اسپویل است"}
                    </p>
                    {!showText && (
                        <button
                            type="button"
                            className="movie-comments__spoiler-btn"
                            onClick={function () {
                                setShowText(true);
                            }}
                        >
                            نمایش
                        </button>
                    )}
                </div>

                <div className="movie-comments__votes">
                    <button type="button" className="movie-comments__vote" onClick={onLike}>
                        <img src={comment.is_liked ? likeFillIcon : likeIcon} alt="لایک" />
                        {comment.like_count}+
                    </button>
                    <button type="button" className="movie-comments__vote" onClick={onDislike}>
                        <img src={comment.is_disliked ? dislikeFillIcon : dislikeIcon} alt="دیسلایک" />
                        {comment.dislike_count}-
                    </button>
                </div>
            </div>
        </div>
    );
}

function ReviewItem({
    review,
    onLike,
    onDislike,
}: {
    review: MovieCommentData;
    onLike: () => void;
    onDislike: () => void;
}) {
    const [showText, setShowText] = useState(!review.is_spoiler);

    return (
        <div className="movie-comments__item">
            <div className="movie-comments__item-header">
                <p className="movie-comments__username">{review.username}</p>
                <p className="movie-comments__date">{review.created_at}</p>
            </div>

            <hr className="movie-comments__divider" />

            <div className="movie-comments__review-body">
                <div className="movie-comments__review-text-wrap">
                    <p className="movie-comments__review-text">
                        {showText ? review.comment : "این نقد حاوی اسپویل است"}
                    </p>
                    {!showText && (
                        <button
                            type="button"
                            className="movie-comments__spoiler-btn"
                            onClick={function () {
                                setShowText(true);
                            }}
                        >
                            نمایش
                        </button>
                    )}
                </div>

                <div className="movie-comments__review-side">
                    <div className="movie-comments__review-score">
                        <img src={UserWaveIcon} alt="" />
                        <p className="movie-comments__review-score-text">
                            <span className="movie-comments__review-score-value">{review.score}</span> / 10
                        </p>
                    </div>
                    <div className="movie-comments__review-votes">
                        <button type="button" className="movie-comments__review-vote" onClick={onLike}>
                            {review.like_count}+
                            <img src={review.is_liked ? likeFillIcon : likeIcon} alt="لایک" />
                        </button>
                        <button type="button" className="movie-comments__review-vote" onClick={onDislike}>
                            {review.dislike_count}-
                            <img src={review.is_disliked ? dislikeFillIcon : dislikeIcon} alt="دیسلایک" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
